import { coachBookingService } from '@/services/coach-booking.service'
import type { CoachTrack } from '@/types/coach'
import type { CoachBooking, CoachChatThread } from '@/types/coach-booking'
import type { CoachGroupSessionWrite } from '@/types/coach-booking'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const bookingKeys = {
  directoryRoot: ['coach-directory'] as const,
  directory: (track: CoachTrack, focus: string[] = [], industry: string | null = null) =>
    ['coach-directory', track, industry, ...focus] as const,
  detail: (profileId: number) => ['coach-directory', profileId] as const,
  alerts: ['coach-availability-alerts'] as const,
  groups: ['coach-group-sessions'] as const,
  coachGroups: ['coach-group-sessions', 'mine'] as const,
  mine: ['coach-bookings', 'mine'] as const,
  myReviews: ['coach-reviews', 'mine'] as const,
  coachReviews: ['coach-reviews', 'coach'] as const,
  publicReviews: (profileId: number) => ['coach-reviews', 'public', profileId] as const,
  externalReviews: (profileId: number) => ['coach-reviews', 'external', profileId] as const,
  assignment: ['coach-assignment'] as const,
  coach: ['coach-bookings', 'coach'] as const,
  conversations: ['coach-conversations'] as const,
  plans: ['coach-plans'] as const,
  notificationPreferences: ['coach-notification-preferences'] as const,
  slots: (profileId: number, typeId: number, startDate: string) =>
    ['coach-slots', profileId, typeId, startDate] as const,
}
export const useCoachDirectory = (
  track: CoachTrack = 'general',
  focus: string[] = [],
  industry: string | null = null,
) =>
  useQuery({
    queryKey: bookingKeys.directory(track, focus, industry),
    queryFn: () => coachBookingService.directory(track, focus, industry),
  })
export const useCoachDetail = (profileId: number | null) =>
  useQuery({
    queryKey: bookingKeys.detail(profileId || 0),
    queryFn: () => coachBookingService.coach(profileId || 0),
    enabled: Boolean(profileId),
  })
export const useCoachAvailabilityAlerts = () =>
  useQuery({
    queryKey: bookingKeys.alerts,
    queryFn: coachBookingService.availabilityAlerts,
  })
export const useCoachGroupSessions = (enabled = true) =>
  useQuery({
    queryKey: bookingKeys.groups,
    queryFn: coachBookingService.groupSessions,
    enabled,
  })
export const useMyCoachGroupSessions = () =>
  useQuery({
    queryKey: bookingKeys.coachGroups,
    queryFn: coachBookingService.myGroupSessions,
  })
export const useMyCoachBookings = (enabled = true) =>
  useQuery({
    queryKey: bookingKeys.mine,
    queryFn: coachBookingService.myBookings,
    enabled,
  })
export const useMyCoachReviews = () =>
  useQuery({
    queryKey: bookingKeys.myReviews,
    queryFn: coachBookingService.myReviews,
  })
export const usePublicCoachReviews = (profileId: number | null) =>
  useQuery({
    queryKey: bookingKeys.publicReviews(profileId || 0),
    queryFn: () => coachBookingService.publicReviews(profileId || 0),
    enabled: Boolean(profileId),
  })
export const usePublicCoachReviewPages = (profileId: number | null, pageSize = 5) =>
  useInfiniteQuery({
    queryKey: [...bookingKeys.publicReviews(profileId || 0), 'pages', pageSize],
    queryFn: ({ pageParam }) =>
      coachBookingService.publicReviewPage(profileId || 0, pageSize, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.offset + lastPage.items.length : undefined,
    enabled: Boolean(profileId),
  })
export const useExternalCoachReviews = (profileId: number | null) =>
  useQuery({
    queryKey: bookingKeys.externalReviews(profileId || 0),
    queryFn: () => coachBookingService.externalReviews(profileId || 0),
    enabled: Boolean(profileId),
  })
export const useExternalCoachReviewPages = (profileId: number | null, pageSize = 5) =>
  useInfiniteQuery({
    queryKey: [...bookingKeys.externalReviews(profileId || 0), 'pages', pageSize],
    queryFn: ({ pageParam }) =>
      coachBookingService.externalReviewPage(profileId || 0, pageSize, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.offset + lastPage.items.length : undefined,
    enabled: Boolean(profileId),
  })
export const useMyCoachAssignment = (enabled = true) =>
  useQuery({
    queryKey: bookingKeys.assignment,
    queryFn: coachBookingService.assignment,
    enabled,
  })

export const useMatchedCoachBackground = (profileId: number | null) =>
  useQuery({
    queryKey: ['matched-coach-background', profileId],
    queryFn: () => coachBookingService.matchedCoachBackground(profileId as number),
    enabled: profileId !== null,
  })
export const useMyCoachMatchRequest = (enabled = true) =>
  useQuery({
    queryKey: ['coach-match-request', 'mine'],
    queryFn: coachBookingService.matchRequest,
    enabled,
  })
export const useMyReceivedCoachReviews = () =>
  useQuery({
    queryKey: bookingKeys.coachReviews,
    queryFn: coachBookingService.coachReviews,
  })
export const useMyReceivedCoachReviewPages = (pageSize = 4) =>
  useInfiniteQuery({
    queryKey: [...bookingKeys.coachReviews, 'pages', pageSize],
    queryFn: ({ pageParam }) => coachBookingService.coachReviewPage(pageSize, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.offset + lastPage.items.length : undefined,
  })
export const useCoachSessions = () =>
  useQuery({
    queryKey: bookingKeys.coach,
    queryFn: coachBookingService.coachSessions,
  })
export const useCoachConversations = (enabled = true) =>
  useQuery({
    queryKey: bookingKeys.conversations,
    queryFn: coachBookingService.conversations,
    enabled,
    // WebSocket is primary. This slower refresh is the cross-instance/network fallback.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })
export const useCoachPlans = () =>
  useQuery({ queryKey: bookingKeys.plans, queryFn: coachBookingService.plans })
export const useCoachNotificationPreferences = () =>
  useQuery({
    queryKey: bookingKeys.notificationPreferences,
    queryFn: coachBookingService.notificationPreferences,
  })
export const useUpdateCoachNotificationPreferences = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.updateNotificationPreferences,
    onSuccess: (preferences) =>
      queryClient.setQueryData(bookingKeys.notificationPreferences, preferences),
  })
}
export const useCoachSlots = (profileId: number | null, typeId: number | null, startDate: string) =>
  useQuery({
    queryKey: bookingKeys.slots(profileId || 0, typeId || 0, startDate),
    queryFn: () => {
      if (!profileId || !typeId) return Promise.resolve([])
      return coachBookingService.slots(profileId, typeId, startDate)
    },
    enabled: Boolean(profileId && typeId),
    staleTime: 5_000,
    gcTime: 10 * 60_000,
    // Availability is shared across members. Refresh only while a picker is mounted;
    // the backend's locked recheck remains the final concurrency guarantee.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    placeholderData: (previousData) => previousData,
  })

function refreshBookings(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: bookingKeys.mine }),
    queryClient.invalidateQueries({ queryKey: bookingKeys.coach }),
    queryClient.invalidateQueries({ queryKey: ['coach-slots'] }),
  ])
}
export function useCreateCoachBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.book,
    onSuccess: () => refreshBookings(qc),
  })
}
export function useFindCoachMatches() {
  return useMutation({ mutationFn: coachBookingService.recommendations })
}
export function useSubmitCoachMatchRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.submitMatchRequest,
    onSuccess: (request) => qc.setQueryData(['coach-match-request', 'mine'], request),
  })
}
export function useSaveCoachReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      bookingId,
      payload,
      existing,
    }: {
      bookingId: number
      payload: Parameters<typeof coachBookingService.createReview>[1]
      existing: boolean
    }) =>
      existing
        ? coachBookingService.updateReview(bookingId, payload)
        : coachBookingService.createReview(bookingId, payload),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: bookingKeys.myReviews }),
        qc.invalidateQueries({ queryKey: ['coach-reviews', 'public'] }),
        qc.invalidateQueries({ queryKey: bookingKeys.directoryRoot }),
      ]),
  })
}

export function useDeleteCoachReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.deleteReview,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: bookingKeys.myReviews }),
        qc.invalidateQueries({ queryKey: bookingKeys.directoryRoot }),
        qc.invalidateQueries({ queryKey: ['coach-reviews', 'public'] }),
      ]),
  })
}

export function useReportCoachReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.reportReview,
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.coachReviews }),
  })
}
export function useSetCoachSaved() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ profileId, saved }: { profileId: number; saved: boolean }) =>
      coachBookingService.setSaved(profileId, saved),
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.directoryRoot }),
  })
}
export function useSetCoachAvailabilityAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      profileId,
      active,
    }: {
      profileId: number
      active: boolean
    }) => coachBookingService.setAvailabilityAlert(profileId, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.alerts }),
  })
}
export function useSetGroupRegistration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, registered }: { id: number; registered: boolean }) =>
      coachBookingService.setGroupRegistration(id, registered),
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.groups }),
  })
}
export function useCreateCoachGroupSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CoachGroupSessionWrite) =>
      coachBookingService.createGroupSession(payload),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: bookingKeys.groups }),
        qc.invalidateQueries({ queryKey: bookingKeys.coachGroups }),
      ]),
  })
}
export function useUpdateCoachGroupSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number
      payload: CoachGroupSessionWrite
    }) => coachBookingService.updateGroupSession(id, payload),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: bookingKeys.groups }),
        qc.invalidateQueries({ queryKey: bookingKeys.coachGroups }),
      ]),
  })
}
export function useCancelCoachBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string | null }) =>
      coachBookingService.cancel(id, reason),
    onSuccess: () => refreshBookings(qc),
  })
}
export function useRescheduleCoachBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      scheduledAt,
      candidateTimezone,
    }: {
      id: number
      scheduledAt: string
      candidateTimezone: string
    }) => coachBookingService.reschedule(id, scheduledAt, candidateTimezone),
    onSuccess: () => refreshBookings(qc),
  })
}
export function useRetryCoachBookingMeeting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.retryMeeting,
    onSuccess: () => refreshBookings(qc),
  })
}
export function useUpdateCoachSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number
      payload: {
        status: CoachBooking['status']
        coach_notes: string | null
        action_items: Array<{ title: string; completed: boolean }>
        shared_resources: Array<{ title: string; url: string }>
      }
    }) => coachBookingService.updateWorkspace(id, payload),
    onSuccess: () => refreshBookings(qc),
  })
}

export function useCancelCoachSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string | null }) =>
      coachBookingService.cancelCoachSession(id, reason),
    onSuccess: () => refreshBookings(qc),
  })
}

export function useRescheduleCoachSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, scheduledAt }: { id: number; scheduledAt: string }) =>
      coachBookingService.rescheduleCoachSession(id, scheduledAt),
    onSuccess: () => refreshBookings(qc),
  })
}

export function useOpenCoachConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.openConversation,
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.conversations }),
  })
}

export function useSendCoachMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: number; body: string }) =>
      coachBookingService.sendMessage(threadId, {
        client_message_id: crypto.randomUUID(),
        body,
      }),
    onSuccess: (message) => {
      qc.setQueryData<CoachChatThread[]>(bookingKeys.conversations, (threads) =>
        threads?.map((thread) =>
          thread.id === message.thread_id
            ? {
                ...thread,
                last_message_at: message.created_at,
                messages: thread.messages.some((item) => item.id === message.id)
                  ? thread.messages
                  : [...thread.messages, message],
              }
            : thread,
        ),
      )
      qc.invalidateQueries({ queryKey: bookingKeys.conversations })
    },
  })
}

export function useMarkCoachConversationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.markConversationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.conversations }),
  })
}

export function useMarkCoachAssignmentSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: coachBookingService.markAssignmentSeen,
    onSuccess: (updated) =>
      qc.setQueryData<CoachChatThread[]>(bookingKeys.conversations, (threads) =>
        threads?.map((thread) =>
          thread.id === updated.id ? { ...thread, new_assignment: false } : thread,
        ),
      ),
  })
}

export function useUpdateCandidateCoachAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      bookingId,
      actionIndex,
      completed,
    }: {
      bookingId: number
      actionIndex: number
      completed: boolean
    }) => coachBookingService.updateCandidateAction(bookingId, actionIndex, completed),
    onSuccess: () => refreshBookings(qc),
  })
}

export function useUpdateCoachPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      candidateUserId,
      payload,
    }: {
      candidateUserId: number
      payload: Parameters<typeof coachBookingService.updatePlan>[1]
    }) => coachBookingService.updatePlan(candidateUserId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.plans }),
  })
}
